import type { ReactionType } from "@rosette/ts/src/business/game/ReactionType.js";
import { useCallback, useEffect, useRef } from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { LottieRefCurrentProps } from "lottie-react";
import Lottie from "lottie-react";
import { Reaction } from "@/components/icon/Reaction.js";
import Image from "next/image";
import { useAccessibility } from "@rosette/web_components/src/accessibility/AccessibilityContext.js";
import type { Seconds } from "@rosette/ts/src/util/units.js";


interface ReactionIconState {
    lottieHovered: boolean;
    lottiePlaying: boolean;
    lottieLoops: number;
    noAnimStartTimeMs: number | null;
}


interface ReactionIconProps {
    className?: string;
    reaction: ReactionType;
    onComplete?: () => void;
    autoPlay?: boolean;
    loopForever?: boolean;
    noAnimDuration?: Seconds;
}


export function ReactionIcon({
    className,
    reaction: reactionType,
    onComplete: onCompleteProp,
    autoPlay,
    loopForever,
    noAnimDuration = 2.5,
}: ReactionIconProps) {
    const accessibility = useAccessibility();
    const reaction = Reaction.get(reactionType);
    const loops = reaction.getOptions().loops ?? 1;
    const lottieData = (!accessibility.useReducedMotion() ? reaction.getLottie() : undefined);

    const ref = useRef<HTMLDivElement | null>(null);
    const lottieRef = useRef<LottieRefCurrentProps | null>(null);

    const stateRef = useRef<ReactionIconState>({
        lottieHovered: false,
        lottiePlaying: !!autoPlay,
        lottieLoops: 0,
        noAnimStartTimeMs: null,
    });

    const onComplete = useCallback(() => {
        const state = stateRef.current;
        const lottie = lottieRef.current;
        state.lottieLoops += 1;
        if (lottie && (loopForever || state.lottieHovered || state.lottieLoops < loops)) {
            lottie.goToAndPlay(0);
            state.lottiePlaying = true;
        } else {
            state.lottiePlaying = false;
        }
        if (state.lottieLoops >= loops) {
            state.lottieLoops = 0;
            if (onCompleteProp) {
                onCompleteProp();
            }
        }
    }, [stateRef, onCompleteProp, loopForever, loops]);

    // Handle timeout for non-animated reactions
    useEffect(() => {
        if (!lottieData && onCompleteProp) {
            const state = stateRef.current;
            if (state.noAnimStartTimeMs === null) {
                state.noAnimStartTimeMs = Date.now();
            }

            const elapsed = Date.now() - state.noAnimStartTimeMs;
            const remaining = Math.max(0, noAnimDuration * 1000 - elapsed);

            const timeoutID = setTimeout(() => {
                onCompleteProp();
            }, remaining);

            return () => {
                clearTimeout(timeoutID);
            };
        }
    }, [lottieData, onCompleteProp, noAnimDuration, stateRef]);

    // Handle hover interactions
    useEffect(() => {
        if (!lottieData)
            return;

        const elem = ref.current;
        const state = stateRef.current;
        if (!elem)
            return;

        const onHover = () => {
            state.lottieHovered = true;
            const lottie = lottieRef.current;
            if (!state.lottiePlaying && lottie) {
                // Reset loops when restarting animation from hover
                state.lottieLoops = 0;
                lottie.goToAndPlay(0);
                state.lottiePlaying = true;
            }
        };
        const onUnhover = () => {
            state.lottieHovered = false;
        };

        elem.addEventListener("mouseenter", onHover);
        elem.addEventListener("mouseleave", onUnhover);
        return () => {
            elem.removeEventListener("mouseenter", onHover);
            elem.removeEventListener("mouseleave", onUnhover);
        };
    }, [lottieData]);

    return (
        <div
            className={cn(
                "inline-block",
                className ?? null,
            )}
            ref={ref}>

            {autoPlay && lottieData && (
                <Lottie
                    lottieRef={lottieRef}
                    autoplay={autoPlay}
                    loop={!!loopForever}
                    onComplete={onComplete}
                    animationData={lottieData} />
            )}

            {(!autoPlay || !lottieData) && (
                <Image src={reaction.getImage()} alt={reactionType.getName()} />
            )}
        </div>
    );
}
